<template>
  <BaseArchive
    v-if="articles && meta"
    class="archive-top"
    :articles="articles"
    :link="link"
    :meta="meta"
    slug="top"
  >
    <template #header>
      <div class="archive-top__header">
        <h1 class="archive-top__title">{{ title }}</h1>

        <div class="archive__switch">
          <ArchiveTopPeriods
            v-if="!isMobile"
            :items="switchItems"
            :title="title"
          />
          <ArchiveTopSwitch
            v-else
            @click="onSwitchClick"
          >
            {{ currentTitle }}
          </ArchiveTopSwitch>
        </div>
      </div>
    </template>
  </BaseArchive>
</template>

<script lang="ts">
  import ArchiveTopPeriods from '../components/ArchiveTopPeriods.vue';
  import ArchiveTopPeriodsMobile from '../components/ArchiveTopPeriodsMobile.vue';
  import ArchiveTopSwitch from '../components/ArchiveTopSwitch.vue';
  import { ARCHIVES_ARTICLES_COUNT_ON_PAGE } from '~/constants';
  import type { TTopPeriod } from '~/types/api/archives';
  import BaseArchive from '~/components/BaseComponents/BaseArchive.vue';
  import { useModal } from '@devhacker/shared/plugins/modal/useModal';
  import { getRouteParam } from '~/types/utils/route';
  import { useSeoStore } from '~/store/seo';
  import { useWindowStore } from '~/store/window';
  import { useHeadWithSeo } from '~/composables/useHeadWithSeo';

  interface IPeriod {
    id: string;
    url: string;
    title: string;
  }

  type TPeriods = {
    [index: string]: IPeriod;
  };

  const periods: TPeriods = {
    week: {
      id: 'week',
      url: '/week/',
      title: 'За неделю',
    },
    month: {
      id: 'month',
      url: '/month/',
      title: 'За месяц',
    },
    year: {
      id: 'year',
      url: '/year/',
      title: 'За год',
    },
  };

  export default defineNuxtComponent({
    name: 'ArchiveTop',

    components: {
      BaseArchive,
      ArchiveTopPeriods,
      ArchiveTopSwitch,
    },

    provide() {
      return {
        eventCategory: () => 'Лучшее',
      };
    },

    scrollToTop: true,

    async setup() {
      const { $api } = useNuxtApp();
      const seoStore = useSeoStore();

      const route = useRoute();
      const topPeriod = getRouteParam(route.params.topPeriod) || 'week';
      const pageNumber = +getRouteParam(route.params.pageNumber || 1);
      const pagesDataRoute = route.fullPath.replace(route.hash, '');

      if (!Object.keys(periods).includes(topPeriod)) {
        throw createError({ statusCode: 404, message: 'Invalid period' });
      }

      const { data, error } = await useAsyncData('top-posts', async () =>
        $api.archives.top({
          period: topPeriod as TTopPeriod,
          page: pageNumber,
          limit: ARCHIVES_ARTICLES_COUNT_ON_PAGE,
        }),
      );

      if (data.value?.data.meta.total_pages === 0) {
        throw createError({ statusCode: 404, message: 'No data' });
      } else if (error.value) {
        throw createError(error.value);
      }

      if (data.value?.data) {
        seoStore.addSeoByPath(pagesDataRoute, data.value.data.seo);
      }

      useHeadWithSeo();

      return {
        articles: data.value?.data.items,
        meta: data.value?.data.meta,
        currentPeriod: topPeriod,
      };
    },

    data: () => ({
      periods,
      title: 'Лучшее',
      baseUrl: '/top',
    }),

    computed: {
      isMobile(): boolean {
        return useWindowStore().isMobile;
      },

      currentTitle(): string {
        const [current] = Object.values(this.periods).filter(
          (period) => period?.id === this.currentPeriod,
        );

        return current?.title || this.title;
      },

      link(): HTMLAnchorElement {
        const { baseUrl, currentPeriod } = this;
        return {
          href: `${baseUrl}/${currentPeriod}`,
        } as HTMLAnchorElement;
      },

      switchItems() {
        const { periods, currentPeriod } = this;

        return Object.values(periods).map((period: IPeriod | undefined) => {
          return {
            id: period?.id,
            title: period?.title,
            isActive: period?.id === currentPeriod,
            link: {
              href: `${this.baseUrl}${period?.url}`,
            } as HTMLAnchorElement,
          };
        });
      },
    },

    methods: {
      getLinkPeriod(period: IPeriod): HTMLAnchorElement {
        return {
          href: `${this.baseUrl}${period.url}`,
        } as HTMLAnchorElement;
      },

      getComponentTab(period: IPeriod) {
        return period.id === this.currentPeriod ? 'span' : 'BHLink';
      },

      getPropsTab(period: IPeriod) {
        return period.id === this.currentPeriod
          ? null
          : {
              link: this.getLinkPeriod(period),
            };
      },

      getClassTab(period: IPeriod) {
        return {
          'tabs__item--current': period.id === this.currentPeriod,
        };
      },

      onSwitchClick() {
        const { switchItems: items, title } = this;

        useModal().open(
          ArchiveTopPeriodsMobile,
          {
            items,
            title,
            closeClickOutside: true,
          },
          {
            transition: 'modal-fade',
          },
        );
      },
    },
  });
</script>

<style lang="scss" scoped>
  :deep() {
    .archive__header {
      padding: 24px 16px 0px;
      @include tablet {
        padding: 0 24px 4px;
      }

      @include big-tablet {
        padding: 0 32px 4px;
      }

      @include desktop {
        padding: 0 40px 4px;
      }
    }
  }
  .archive-top {
    &__header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 0;
    }

    &__title {
      font-family: $headline-font--1;
      font-weight: 400;
      @include headline-small-text;

      @include tablet {
        font-size: 32px;
        line-height: 42px;
      }
    }
  }

  .tabs {
    display: flex;
    flex-wrap: nowrap;

    &__item {
      font-family: $headline-font--1;
      font-weight: 500;
      @include smallText;
      color: $icon-color--1;
      padding: $spacing-3 $spacing-4;
      border: 1px solid transparent;

      &:hover {
        &:not(.tabs__item--current) {
          color: $mainBlack;
        }
      }

      &--current {
        border-color: $active-color--1;
        color: $active-color--1;
        cursor: default;
        border-radius: 50%;
      }
    }
  }
</style>
